import {Box, Button, Dialog, DialogActions, DialogContent, Stack, Switch, Tooltip, Typography} from '@mui/material';
import {
  type PlanSettingsShortTermGrowthModelTypeEnum,
  type PlanSettings,
  PlanSettingsCountryEnum,
} from 'client/bioplanModels/plan-settings';
import DialogTitleWithClose from 'components/Generic/DialogTitle';
import GenericTextField from 'components/Generic/TextField';
import {LoadingOverlay} from 'components/Loading';
import {SingleSelect} from 'components/Shared/SingleSelect';
import NumberFormatCustom from 'helpers/NumberFormatter';
import {useGetPlanSettings, useGetRegions} from 'hooks/swr';
import useParseURL from 'hooks/useParseURL';
import {SWRKeyEnum} from 'interfaces';
import {useState, type Dispatch, type SetStateAction, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';
import {PlanSettingsCountrySelector} from './PlanSettingsCountry';
import {PlanSettingsNOKtoUSD} from './PlanSettingsNOKtoUSD';

const MAX_SHORT_TERM_TRANSITION_START = 30;
const MAX_SHORT_TERM_TRANSITION_END = 90;

const validator = (data: PlanSettings, key: keyof PlanSettings) => {
  switch (key) {
    case 'shortTermGrowthTransitionStart': {
      if (data.shortTermGrowthTransitionStart > MAX_SHORT_TERM_TRANSITION_START) {
        return 'planSettingsshortTermGrowthTransitionStartHelperText';
      } else if (data.shortTermGrowthTransitionStart >= data.shortTermGrowthTransitionStop) {
        return 'planSettingsshortTermGrowthTransitionStartEndRageHelperText';
      }
      break;
    }

    case 'shortTermGrowthTransitionStop': {
      if (data.shortTermGrowthTransitionStop > MAX_SHORT_TERM_TRANSITION_END) {
        return 'planSettingsshortTermGrowthTransitionStopHelperText';
      } else if (data.shortTermGrowthTransitionStop <= data.shortTermGrowthTransitionStart) {
        return 'planSettingsshortTermGrowthTransitionStartEndRageHelperText';
      }
      break;
    }
  }
};

const shortTermRangeFields: Array<{
  min: number;
  max: number;
  fieldName: keyof PlanSettings;
}> = [
  {min: 0, max: MAX_SHORT_TERM_TRANSITION_START, fieldName: 'shortTermGrowthTransitionStart'},
  {min: 0, max: MAX_SHORT_TERM_TRANSITION_END, fieldName: 'shortTermGrowthTransitionStop'},
];

export const PlanSettingsModal = ({open, setOpen}: {setOpen: Dispatch<SetStateAction<boolean>>; open: boolean}) => {
  const {planId} = useParseURL();
  const {t} = useTranslation('common');
  const {data: regions} = useGetRegions({resourceId: planId});
  const [planSettings, setPlanSettings] = useState<PlanSettings | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const {data: settings, error} = useGetPlanSettings({resourceId: planId});
  useEffect(() => {
    if (settings) {
      setPlanSettings(settings);
    }
  }, [settings]);

  const switchRegionsDisabled = regions && settings ? !settings.hideRegions && regions?.length >= 2 : false;

  const handleClose = () => {
    setOpen(false);
  };

  if (!error && !planSettings) {
    return <LoadingOverlay />;
  }

  const isFormInvalid =
    !planSettings ||
    Array.from<keyof PlanSettings>(Object.keys(planSettings) as Array<keyof PlanSettings>).some((key) =>
      validator(planSettings, key),
    );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false}>
      <DialogTitleWithClose onClose={handleClose}>{t('planSettings')}</DialogTitleWithClose>
      <DialogContent sx={{padding: 1}}>
        {planSettings && (
          <Stack>
            <Stack p={2} spacing={1}>
              <PlanSettingsCountrySelector {...{planSettings, setPlanSettings}} />
              {planSettings.country === PlanSettingsCountryEnum.Chile && (
                <PlanSettingsNOKtoUSD {...{planSettings, setPlanSettings}} />
              )}
              <Stack direction={'row'} justifyContent="space-between" alignItems="flex-start" width="100%">
                <Stack spacing={1}>
                  <Typography variant="h6">{t('regions')}</Typography>
                  <Typography color="text.disabled" variant="caption">
                    {t('plan and optimize across multiple regions')}
                  </Typography>
                  {switchRegionsDisabled && (
                    <Typography variant="caption" color="error.main">
                      {t('cant disble regions')}
                    </Typography>
                  )}
                </Stack>
                <Box display="flex" justifyContent="flex-end" minWidth={200}>
                  <Switch
                    checked={!planSettings.hideRegions}
                    onChange={(event, checked) => {
                      setPlanSettings({...planSettings, hideRegions: !checked});
                    }}
                    data-testid="regions-switch"
                    disabled={switchRegionsDisabled}
                  />
                </Box>
              </Stack>
            </Stack>
            <Stack p={2} pt={0} spacing={1}>
              <Stack direction={'row'} justifyContent="space-between" alignItems="flex-start" width="100%">
                <Stack spacing={1}>
                  <Typography variant="h6">{t('useShortTermTemperature')}</Typography>
                  <Typography color="text.disabled" variant="caption">
                    {t('useShortTermTemperatureCaption')}
                  </Typography>
                </Stack>
                <Box display="flex" justifyContent="flex-end" minWidth={200}>
                  <Switch
                    checked={planSettings.useShortTermTemperature}
                    onChange={(event, checked) => {
                      setPlanSettings({...planSettings, useShortTermTemperature: checked});
                    }}
                    data-testid="short-term-temperature-switch"
                    disabled={switchRegionsDisabled}
                  />
                </Box>
              </Stack>
            </Stack>
            <Stack p={2} pt={0} spacing={1}>
              <Stack direction={'row'} justifyContent="space-between" alignItems="flex-start" width="100%">
                <Stack spacing={1}>
                  <Typography variant="h6">{t('planSettingsShortTermModelType')}</Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t('planSettingsShortTermModelTypeCaption')}
                  </Typography>
                </Stack>
                <Box display="flex" justifyContent="flex-end" minWidth={200}>
                  <SingleSelect
                    onClick={(shortTermGrowthModelType) => {
                      setPlanSettings({...planSettings, shortTermGrowthModelType});
                    }}
                    selectedKey={planSettings.shortTermGrowthModelType}
                    options={
                      new Map<PlanSettingsShortTermGrowthModelTypeEnum, string>([
                        ['simple_ewma', t('simple_ewma')],
                        ['dynamic_ewma', t('dynamic_ewma')],
                        ['ngr_residual_ets', t('ngr_residual_ets')],
                        ['use_tenant_settings', t('use_company_settings')],
                      ])
                    }
                  />
                </Box>
              </Stack>
            </Stack>
            <Stack p={2} pt={0}>
              <Stack spacing={1}>
                <Typography variant="h6">{t('planSettingsShortTermGrowthTransition')}</Typography>
              </Stack>
              <Stack pt={2} direction={'row'} spacing={1}>
                {shortTermRangeFields.map((field) => {
                  const validationErrorCode = validator(planSettings, field.fieldName);

                  return (
                    <Tooltip title={t(`planSettings${field.fieldName}Tooltip`)} key={field.fieldName}>
                      <GenericTextField
                        sx={{width: 250}}
                        fullWidth={false}
                        label={t(`planSettings${field.fieldName}`)}
                        value={planSettings[field.fieldName] ?? ''}
                        onChange={(event) => {
                          const value = parseInt(event.target.value);
                          if (!isNaN(value)) {
                            setPlanSettings({
                              ...planSettings,
                              [field.fieldName]: value,
                            });
                          }
                        }}
                        error={!!validationErrorCode}
                        helperText={validationErrorCode ? t(validationErrorCode) : undefined}
                        InputProps={{
                          inputComponent: NumberFormatCustom as (props) => JSX.Element,
                          endAdornment: t('days'),
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
              <Stack flexGrow={1} />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={!settings || isLoading || isFormInvalid}
          autoFocus
          variant="contained"
          onClick={async () => {
            if (planSettings) {
              setIsLoading(true);
              await mutate(
                [planId, SWRKeyEnum.GET_PLAN_SETTINGS],
                backendCommunicator
                  .updatePlanSettings(planId, planSettings)
                  .then((response) => response.data)
                  .finally(() => {
                    setIsLoading(false);
                    setOpen(false);
                  }),
              );
            }
          }}
          loading={isLoading}
          loadingPosition="end"
          data-testid="plan-settings-save-btn"
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
